import { useEffect, useMemo, useRef, useState } from 'react'

import { getItemsIndexes } from '@aytm/app/stats/page/question-views/info-view/components/compare-items/lib/compare-items-utils'
import { calcConceptQuestions } from '@aytm/app/stats/page/question-views/info-view/components/compare-items/lib/concept-questions'
import {
  calcDownloadParams,
  calcExportData,
  calcExportPartial,
  calcReportParamsSection
} from '@aytm/app/stats/page/question-views/info-view/components/compare-items/lib/export-params'
import { resolveReportParams } from '@aytm/app/stats/page/question-views/info-view/components/compare-items/lib/report-params'

// Owns the compare-items block state (selection, preset, ranking, toggles, split-by), seeded
// from the cts<key> report-param section and re-seeded whenever that section changes outside
// the block.
//
// The page view never derives any of this itself: it receives an api on mount and pulls the
// payloads it needs (model state, report-param section, export data, concept questions) when a
// change is announced through onStateChange.
export default function useCompareItemsState({
  params,
  format,
  block,
  items,
  attributes,
  mainAttributePosition,
  splitBy,
  template,
  batches,
  answer,
  questionId,
  questionKey,
  id,
  link,
  percentDecimals,
  onReady,
  onStateChange
}) {
  const needsParamsUpdate = useRef(false)

  const [state, setState] = useState(() => {
    const resolved = resolveReportParams({ section: params?.cts, format, block, items, mainAttributePosition })
    needsParamsUpdate.current = resolved.needsUpdate
    return { ...resolved.state, splitBy }
  })

  // every input the api payloads are built from, refreshed each render so the pull is current
  const snapshot = useRef()
  snapshot.current = {
    ...state,
    params,
    format,
    block,
    items,
    attributes,
    mainAttributePosition,
    template,
    batches,
    answer,
    questionId,
    questionKey,
    id,
    link,
    percentDecimals
  }

  const api = useMemo(
    () => ({
      getState: () => {
        const { concepts, preset, attribute, reverse, analysisMode, showImages, fullTexts, splitBy } = snapshot.current
        const { items, attributes, format } = snapshot.current

        return {
          items,
          attributes,
          format,
          splitBy,
          concepts,
          preset,
          attribute,
          reverse,
          analysisMode,
          showImages,
          fullTexts
        }
      },
      getReportParamsSection: () => calcReportParamsSection(snapshot.current),
      getReportExportData: () => calcExportData(snapshot.current),
      getExportData: () => calcExportPartial(snapshot.current),
      getDownloadParams: () => calcDownloadParams(snapshot.current),
      getConceptQuestions: () => {
        const { items, concepts, preset, attribute, reverse } = snapshot.current

        return calcConceptQuestions({
          ...snapshot.current,
          itemIndexes: getItemsIndexes(items, concepts, preset, attribute, reverse)
        })
      }
    }),
    []
  )

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  const sectionKey = JSON.stringify(params?.cts ?? null)
  const lastSectionKey = useRef(sectionKey)
  useEffect(() => {
    if (lastSectionKey.current === sectionKey) return
    lastSectionKey.current = sectionKey

    const { params, format, block, items, mainAttributePosition } = snapshot.current
    const resolved = resolveReportParams({ section: params?.cts, format, block, items, mainAttributePosition })
    needsParamsUpdate.current = needsParamsUpdate.current || resolved.needsUpdate
    setState(current => ({ ...current, ...resolved.state }))
  }, [sectionKey])

  const lastSplitBy = useRef(splitBy)
  useEffect(() => {
    if (lastSplitBy.current === splitBy) return
    lastSplitBy.current = splitBy
    setState(current => ({ ...current, splitBy }))
  }, [splitBy])

  // The format's showImages reads "the items carry an image". Only its flip moves the toggle: the first image
  // turns it on and losing the last turns it off, while in between the researcher's own choice stands.
  const hasImages = Boolean(format?.showImages)
  const lastHasImages = useRef(hasImages)
  useEffect(() => {
    if (lastHasImages.current === hasImages) return
    lastHasImages.current = hasImages
    setState(current => (current.showImages === hasImages ? current : { ...current, showImages: hasImages }))
  }, [hasImages])

  // The reason tells the view what the change means: 'seed' is the resolved starting state,
  // 'data' a fresh answer or batch set, 'state' a change the researcher made.
  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  const announced = useRef(false)
  useEffect(() => {
    const reason = announced.current ? 'data' : 'seed'
    announced.current = true

    onStateChangeRef.current?.(reason, { needsUpdate: needsParamsUpdate.current })
    needsParamsUpdate.current = false
  }, [items, attributes, format])

  const lastState = useRef(state)
  useEffect(() => {
    if (lastState.current === state) return
    lastState.current = state

    onStateChangeRef.current?.('state', { needsUpdate: needsParamsUpdate.current })
    needsParamsUpdate.current = false
  }, [state])

  const handlers = useMemo(
    () => ({
      onAnalysisModeUpdated: analysisMode => setState(current => ({ ...current, analysisMode })),
      onShowImagesUpdated: showImages => setState(current => ({ ...current, showImages })),
      onFullTextsUpdated: fullTexts => setState(current => ({ ...current, fullTexts })),
      onChangeSplitBy: splitBy => setState(current => ({ ...current, splitBy })),
      onSelectedItemsUpdated: (update, options) =>
        setState(current => ({
          ...current,
          preset: null != update.presetIndex && 0 <= update.presetIndex ? options.presets[update.presetIndex]?.id : '',
          concepts: (update.selectedIndexes || []).map(itemIndex => options.items[itemIndex].position),
          attribute:
            null != update.sortByAttributeIndex && 0 <= update.sortByAttributeIndex
              ? options.attributes[update.sortByAttributeIndex]?.position
              : -1,
          reverse: update.reverseSortOrder
        }))
    }),
    []
  )

  return { ...state, ...handlers, api }
}
